/**
 * Your inbox on the server: texts from players you've swapped numbers with, wherever you both are (and kept while
 * you're away). The game keeps a socket open to it with this device's secret key, saves what arrives, then acks it.
 * Sending goes to /send with your id and key (the server checks the key against your inbox: nobody can be you).
 */
import { socketFailed, socketOpened } from './health';
import type { RaceAnswer, RaceBody } from './raceproto';
import type { BalanceSnap } from './snap';
import type { HomeListing, VisitAsk, InboxData, InboxMsg, InboxPush, LiftAnswer, LiftStep, SendBody, VisitBody, VisitStatus } from './protocol';

/** this device's secret for its inbox (per player id: ?pid= lives in one browser get their own) */
export function inboxKey(id: string): string {
  const k = `unfold.playerKey.${id}`;
  try {
    let key = localStorage.getItem(k);
    if (!key) { key = `${crypto.randomUUID()}${crypto.randomUUID()}`.replace(/-/g, ''); localStorage.setItem(k, key); }
    return key;
  } catch { return `${crypto.randomUUID()}${crypto.randomUUID()}`.replace(/-/g, ''); }
}

type Dist<T> = T extends unknown ? Omit<T, 'id' | 'key' | 'name'> : never;
export type RaceStep = Dist<RaceBody>;

export class Inbox {
  private ws: WebSocket | null = null;
  private retry: ReturnType<typeof setTimeout> | null = null;
  private closed = false;
  private readonly http: string;

  constructor(private o: { url: string; id: string; key: string; name: () => string;
    /** a delivery: save it, return true (then it's acked); false: not wanted (from a stranger), acked anyway */
    onMail: (m: InboxMsg) => boolean }) {
    this.http = o.url.replace(/^ws/, 'http').replace(/\/$/, '');
    this.open();
  }

  get connected() { return this.ws?.readyState === WebSocket.OPEN; }
  /** the server's news that isn't mail: your lift with another player, as it is now (set by the street) */
  onPush: ((m: InboxPush) => void) | null = null;

  private open() {
    if (this.closed) return;
    let ws: WebSocket;
    try { ws = new WebSocket(`${this.o.url.replace(/\/$/, '')}/inbox/${this.o.id}?key=${this.o.key}`); } catch { return; }
    this.ws = ws;
    let opened = false;
    ws.onopen = () => { opened = true; socketOpened(); ws.send(JSON.stringify({ t: 'sync', gifts: true })); }; // (send me what's been waiting; this game takes Support's gifts)
    ws.onmessage = (e) => {
      let m: InboxMsg | InboxPush;
      try { m = JSON.parse(String(e.data)) as InboxMsg | InboxPush; } catch { return; }
      if (m.t === 'lift') { this.onPush?.(m); return; }
      if (m.t !== 'mail') return;
      this.o.onMail(m);
      ws.send(JSON.stringify({ t: 'ack', seq: m.seq, ...(m.from === 'unfold:support' && m.data.k === 'money' ? { gift: true } : {}) }));
    };
    ws.onclose = () => { if (!opened) socketFailed(); if (this.ws === ws) { this.ws = null; if (!this.closed) this.retry = setTimeout(() => this.open(), 5000); } };
  }

  /** where you are in the game (the area, where, the spot; `out`: on the streets, where the spot is on the map), for the
   *  players in `share` (your contacts) to see */
  async shareLoc(l: { hood: string; label: string; x: number; z: number; out: boolean }, share: string[]) {
    try { await fetch(`${this.http}/loc`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: this.o.id, key: this.o.key, ...l, share }) }); } catch { /* offline: next time */ }
  }
  /** where a contact is (null: not playing, or they haven't you in theirs) */
  async locOf(id: string): Promise<{ hood: string; label: string; x: number; z: number; out?: boolean; at: number } | null> {
    try { const r = await fetch(`${this.http}/loc/${encodeURIComponent(id)}?from=${encodeURIComponent(this.o.id)}&key=${encodeURIComponent(this.o.key)}`); return r.ok ? await r.json() : null; } catch { return null; }
  }

  /** a step of a lift with another player: the server decides, and answers with the lift as it now is (or why not) */
  async lift(b: LiftStep): Promise<LiftAnswer | null> {
    try {
      const r = await fetch(`${this.http}/lift`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...b, id: this.o.id, key: this.o.key, name: this.o.name() }) });
      return await r.json() as LiftAnswer;
    } catch { return null; }
  }
  /** a step of a race with another player (invite, answer, call it off): the race as the server now has it */
  async race(b: RaceStep): Promise<RaceAnswer | null> {
    try {
      const r = await fetch(`${this.http}/race`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...b, id: this.o.id, key: this.o.key, name: this.o.name() }) });
      return await r.json() as RaceAnswer;
    } catch { return null; }
  }
  /** a balance snapshot (net/snap.ts) for the server's record (keepalive: it goes even as the tab closes) */
  snap(snap: Omit<BalanceSnap, 'at' | 'src'>) {
    try { void fetch(`${this.http}/snap`, { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: this.o.id, key: this.o.key, snap }) }).catch(() => {}); } catch { /* offline */ }
  }
  /** the race's own socket */
  raceUrl(rid: string) { return `${this.o.url.replace(/\/$/, '')}/race/${rid}?id=${encodeURIComponent(this.o.id)}&key=${encodeURIComponent(this.o.key)}`; }
  /** your home in the Visit app (null: none to visit), as your game has it */
  async publishHome(home: { id: string; label: string; area: string; style: string } | null, country: string, region: string): Promise<boolean> {
    try { return (await fetch(`${this.http}/home`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: this.o.id, key: this.o.key, home, country, region }) })).ok; } catch { return false; }
  }
  /** everyone's homes (not yours), and where your requests stand; null: offline */
  async homes(): Promise<{ homes: HomeListing[]; mine: VisitStatus[]; asks: VisitAsk[]; open: boolean } | null> {
    try { const r = await fetch(`${this.http}/homes?id=${encodeURIComponent(this.o.id)}&key=${encodeURIComponent(this.o.key)}`); return r.ok ? await r.json() : null; } catch { return null; }
  }
  /** a visit step: ask, answer (the owner), cancel, enter (at the door: the house, only with a yes) */
  async visit(op: VisitBody['op'], to: string, yes?: boolean): Promise<{ ok: boolean; why?: string; status?: string; home?: { id: string; label: string; area: string; style: string; country: string } } | null> {
    try {
      const r = await fetch(`${this.http}/visit`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: this.o.id, key: this.o.key, name: this.o.name(), to, op, ...(yes !== undefined ? { yes } : {}) }) });
      return await r.json();
    } catch { return null; }
  }

  /** a balance snapshot, waited for (a bank transfer is checked against it) */
  async snapNow(snap: unknown) { try { await fetch(`${this.http}/snap`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: this.o.id, key: this.o.key, snap }) }); } catch { /* the check will say so */ } }
  /** a picture or a voice note, kept for Global chat: resolves to its id (null: it didn't go) */
  async keepMedia(mime: string, b64: string): Promise<string | null> {
    const id = `gc-${crypto.randomUUID().replace(/-/g, '').slice(0, 20)}`;
    try { const r = await fetch(`${this.http}/reel`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ from: this.o.id, key: this.o.key, id, mime, video: b64 }) }); return r.ok ? id : null; } catch { return null; }
  }
  /** to another player's inbox; resolves to whether the server took it */
  async send(to: string, data: InboxData): Promise<boolean> {
    const body: SendBody = { from: this.o.id, key: this.o.key, to, name: this.o.name(), data };
    try { const r = await fetch(`${this.http}/send`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }); return r.ok; } catch { return false; }
  }

  /** a voice note to another player: the audio and the note in one go; resolves to its id (null: it didn't go) */
  async sendVoice(to: string, rec: { blob: Blob; secs: number; mime: string }): Promise<string | null> {
    const id = crypto.randomUUID().replace(/-/g, '').slice(0, 20);
    const bytes = new Uint8Array(await rec.blob.arrayBuffer());
    let bin = '';
    for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    const body = { from: this.o.id, key: this.o.key, to, name: this.o.name(), id, secs: rec.secs, mime: rec.mime, audio: btoa(bin) };
    try { const r = await fetch(`${this.http}/vn`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }); return r.ok ? id : null; } catch { return null; }
  }
  /** a photo from the game's camera (a data: URL) to another player: the picture and the note in one go; resolves to its id (null: it didn't go) */
  async sendPic(to: string, url: string): Promise<string | null> {
    const id = crypto.randomUUID().replace(/-/g, '').slice(0, 20);
    const m = /^data:(image\/(?:jpeg|webp|png));base64,(.+)$/.exec(url);
    if (!m) return null;
    const body = { from: this.o.id, key: this.o.key, to, name: this.o.name(), id, mime: m[1], image: m[2] };
    try { const r = await fetch(`${this.http}/pic`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }); return r.ok ? id : null; } catch { return null; }
  }
  private voices = new Map<string, string>();
  /** why the last voice note (or chat photo) didn't load: gone from the server, this device's key refused, or no network */
  voiceFail: 'gone' | 'key' | 'net' | null = null;
  /** a voice note's audio (yours or theirs: both are kept in your inbox), as a URL to play */
  async voiceUrl(id: string): Promise<string | null> {
    const have = this.voices.get(id);
    if (have) return have;
    try {
      const r = await fetch(`${this.http}/vn/${this.o.id}/${id}?key=${this.o.key}`);
      if (!r.ok) { this.voiceFail = r.status === 404 ? 'gone' : r.status === 403 ? 'key' : 'net'; console.warn('voice note', id, r.status); return null; }
      this.voiceFail = null;
      const url = URL.createObjectURL(await r.blob());
      this.voices.set(id, url);
      return url;
    } catch { this.voiceFail = 'net'; return null; }
  }

  dispose() { this.closed = true; if (this.retry) clearTimeout(this.retry); this.ws?.close(); }
}
